import { Box, CircularProgress, Container, IconButton, Paper, Slider, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import ScreenBanner from "../../../../../components/ScreenBanner";
import TableCellPrinter from "../../../../../components/TableCellPrinter";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useCallback, useState } from "react";
import DetalheDialog from "./DetalheDialog";
import FiltrosCabecalho from "./FiltrosCabecalho";
import axiosClient from "../../../../../lib/axios";
import { useQuery } from "react-query";
import { IDataRelatorioGiroEstoque } from "../../../../../types/api/estoque/relatorio-giro-estoque";
import IRequest from "../../../../../types/IRequest";
import moment, { Moment } from "moment";
import verificarIdsValidos from "../../../../../helpers/verificarIdsValidos";
import { extrairIds } from "../../../../../helpers/extrairIdsArray";
import useGenericDebounce from "../../../../../lib/useGenericDebounce";

export default function GiroDeEstoque() {

    const sliderMarks = [
        { value: 0, label: '0' },
        { value: 1, label: '' },
        { value: 2, label: '' },
        { value: 3, label: '' },
        { value: 4, label: '' },
        { value: 5, label: '5' },
        { value: 6, label: '' },
        { value: 7, label: '' },
        { value: 8, label: '' },
        { value: 9, label: '' },
        { value: 10, label: '10' },
    ];

    const [openModal, setOpenModal] = useState<number | null>(null)
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtros, setFiltros] = useState<TFiltrosState>({
        pesquisar: "",
        giro: [0, 10],
        loja: [{ id: null, nome: "Todas" }],
        setor: [{ id: null, nome: "Todos" }],
        departamento: [{ id: null, nome: "Todos" }],
        dataInicial: null,
        dataFinal: null
    })

    const dbcFilter = useGenericDebounce<TFiltrosState>(filtros)

    //quries
    const { data: resultGiroEstoque, isLoading: isGiroEstoque } = useQuery({
        queryKey: ["relatorio-giro-estoque", page, rowsPerPage, dbcFilter],
        queryFn: () => axiosClient.get("/estoque/relatorio-giro-estoque", {
            params: {
                page: page,
                limit: rowsPerPage,
                pesquisar: dbcFilter.pesquisar,
                giro_maior_que: dbcFilter.giro[0],
                giro_menor_que: dbcFilter.giro[1],
                ...(dbcFilter.dataInicial ? { periodo_inicio: dbcFilter.dataInicial.format('YYYY-MM') } : {}),
                ...(dbcFilter.dataFinal ? { periodo_fim: dbcFilter.dataFinal.format('YYYY-MM') } : {}),
                ...(verificarIdsValidos(dbcFilter.loja) ? { lojas: extrairIds(dbcFilter.loja) } : {}),
                ...(verificarIdsValidos(dbcFilter.setor) ? { setores: extrairIds(dbcFilter.setor) } : {}),
                ...(verificarIdsValidos(dbcFilter.departamento) ? { departamentos: extrairIds(dbcFilter.departamento) } : {})
            }
        })
    });
    const { reqGiroEstoque, dataGiroEstoque }: IGiroEstoque = {
        reqGiroEstoque: resultGiroEstoque?.data,
        dataGiroEstoque: resultGiroEstoque?.data.data || []
    }
    //

    //funções manipuladoras
    const handleOpenModal = useCallback((id: number) => {
        setOpenModal(id)
    }, [])

    const handleCloseModal = useCallback(() => {
        setOpenModal(null)
    }, [])

    const handleChangePage = (
        _: any,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function valuetext(value: number) {
        return `${value}°C`;
    }
    //

    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Estoque" />

            <Container sx={{ mt: 6 }}>
                <Typography variant="h5" align="center" mb={2}>Análise de giro de estoque</Typography>

                <FiltrosCabecalho
                    {...{ filtros }}
                    onChangeFiltros={(prop, value) => setFiltros(prevState => ({ ...prevState, [prop]: value }))}
                />

                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" p={2}>
                        <Typography variant="h5" mt={1}>Produtos</Typography>

                        <Box sx={{ width: 300 }} mr={3} display="flex">
                            <Typography variant="caption" width={180} mr={2} mt={1}>Intervalo de giro:</Typography>
                            <Slider
                                value={filtros.giro}
                                onChange={(_, novoValor) => setFiltros(prevState => ({ ...prevState, giro: novoValor as number[] }))}
                                valueLabelDisplay="off"
                                getAriaValueText={valuetext}
                                min={0}
                                max={10}
                                step={1}
                                marks={sliderMarks}
                            />
                        </Box>
                    </Box>

                    {
                        isGiroEstoque &&
                        <Box display="flex" justifyContent="center" my={2}>
                            <CircularProgress />
                        </Box>
                    }

                    {
                        !isGiroEstoque &&
                        <Table size="small" sx={{ marginTop: 2 }}>
                            <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 100 }}>
                                <TableRow>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>
                                        <Box style={{ marginTop: 8 }}>Nome</Box>
                                        <Typography variant="caption">Departamento / Setor</Typography>
                                    </TableCell>
                                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold' }}>Saldo atual</TableCell>
                                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', border: 0 }}>Qtd. total de vendas</TableCell>
                                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold' }}>Última venda</TableCell>
                                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold' }}>Giro</TableCell>
                                    <TableCell rowSpan={2}></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="center"><Typography component="div" variant="caption">Saldo médio</Typography></TableCell>
                                    <TableCell align="center"><Typography component="div" variant="caption">Venda média</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    dataGiroEstoque.map((produto) => {
                                        return (
                                            <TableRow
                                                key={produto.produto_id}
                                                onClick={() => handleOpenModal(produto.produto_id)}
                                                sx={{
                                                    cursor: "pointer",
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(255,244,229,1)'
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <TableCellPrinter
                                                        label={produto.produto_id + ' - ' + produto.nome}
                                                        subLabel={`${produto.departamento} / ${produto.setor}`}
                                                    />
                                                </TableCell>

                                                <TableCell align="center">
                                                    {produto.estoque_atual}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {produto.estoque_medio}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {produto.venda_media}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {produto.ultima_venda ? moment(produto.ultima_venda, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {produto.giro || "0"}
                                                </TableCell>

                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    }

                    <TablePagination
                        component="div"
                        count={reqGiroEstoque?.total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Linhas por página"
                    />
                </TableContainer>
            </Container>

            {
                openModal &&
                <DetalheDialog
                    productId={openModal}
                    preFilledLoja={filtros.loja}
                    open={Boolean(openModal)}
                    handleClose={handleCloseModal}
                />
            }
        </>
    )
}

interface IGiroEstoque {
    reqGiroEstoque: IRequest<IDataRelatorioGiroEstoque>
    dataGiroEstoque: IDataRelatorioGiroEstoque[]
}

export type TFiltrosState = {
    giro: number[],
    pesquisar: string,
    loja: TFiltroObj[],
    setor: TFiltroObj[],
    departamento: TFiltroObj[],
    dataInicial: Moment | null,
    dataFinal: Moment | null
}

export type TFiltroObj = {
    id: number | null,
    nome: string
}